import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import Information from "./Information";
import "../css/reset.css";
import "../css/common.css";
import "../css/map.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

function Map() {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [selectedCadastral, setSelectedCadastral] = useState(null);
  const [transactionData, setTransactionData] = useState(null);
  const [buildingData, setBuildingData] = useState(null);
  const [zoningData, setZoningData] = useState(null);
  const [urbanPlanningData, setUrbanPlanningData] = useState(null);
  const [townData, setTownData] = useState(null);
  const [villageData, setVillageData] = useState(null);
  const [buildingTypes, setBuildingTypes] = useState([]);
  const [constructionCosts, setConstructionCosts] = useState({});
  const [loading, setLoading] = useState(false);
  const [showInformation, setShowInformation] = useState(false);
  const [infoWindow, setInfoWindow] = useState(null);
  const polygonsRef = useRef([]);
  const markersRef = useRef([]);
  const loadingRef = useRef(false); // 로딩 상태를 동기적으로 추적

  // 용도지역에 적용 가능한 건축 유형의 공사비 가져오기
  const fetchConstructionCostsForZoning = useCallback(async (zoneName) => {
    try {
      // 해당 용도지역에 적용 가능한 건축 유형 필터링
      const applicableTypes = buildingTypes.filter((type) => {
        return (
          type.applicableZoning &&
          type.applicableZoning.length > 0 &&
          type.applicableZoning.includes(zoneName)
        );
      });

      // 각 건축 유형의 공사비 가져오기
      const costsData = {};
      
      for (const buildingType of applicableTypes) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/admin/construction-costs`,
            {
              params: { buildingType: buildingType.code },
            }
          );

          const costs = response.data.costs || [];
          
          // 모든 공종의 totalCost 합산 (원/㎡)
          const totalCostPerSqm = costs.reduce((sum, cost) => {
            return sum + (cost.totalCost || 0);
          }, 0);

          costsData[buildingType.code] = {
            buildingType,
            costs,
            totalCostPerSqm,
          };
        } catch (error) {
          console.error(
            `Error fetching costs for ${buildingType.code}:`,
            error
          );
        }
      }

      setConstructionCosts(costsData);
    } catch (error) {
      console.error("Error fetching construction costs:", error);
    }
  }, [buildingTypes]);

  // 건축 유형 목록 가져오기
  useEffect(() => {
    const fetchBuildingTypes = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/admin/building-types`);
        setBuildingTypes(response.data || []);
      } catch (error) {
        console.error("Error fetching building types:", error);
      }
    };
    fetchBuildingTypes();
  }, []);

  // 용도지역과 건축 유형이 모두 준비되면 공사비 가져오기
  useEffect(() => {
    if (zoningData && buildingTypes.length > 0) {
      const zoning = Array.isArray(zoningData) ? zoningData[0] : zoningData;
      const zoneName = zoning?.luse || zoning?.zoneType || zoning?.name;
      
      if (zoneName) {
        fetchConstructionCostsForZoning(zoneName);
      }
    }
  }, [zoningData, buildingTypes, fetchConstructionCostsForZoning]);

  useEffect(() => {
    // Google Maps 스크립트 로드
    const loadGoogleMaps = () => {
      // 이미 로드되어 있는지 확인
      if (window.google && window.google.maps && window.google.maps.Map) {
        initializeMap();
        return;
      }

      // 이미 스크립트가 로드 중인지 확인
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        existingScript.addEventListener('load', initializeMap);
        return;
      }

      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        console.error("Google Maps API key is not set. Please set REACT_APP_GOOGLE_MAPS_API_KEY in .env file.");
        alert("Google Maps API key is not set.");
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.google && window.google.maps) {
          initializeMap();
        } else {
          console.error("Google Maps API failed to load properly.");
        }
      };
      script.onerror = () => {
        console.error("Failed to load Google Maps script");
        alert("An error occurred while loading Google Maps.");
      };
      document.head.appendChild(script);
    };

    const initializeMap = () => {
      if (!mapRef.current) {
        console.error("mapRef.current is not available.");
        return;
      }

      if (!window.google || !window.google.maps || !window.google.maps.Map) {
        console.error("Google Maps API has not been loaded yet.");
        return;
      }

      try {
        // Dubai center coordinates
        const dubaiCenter = { lat: 25.202858375, lng: 55.24999334 };

        const mapInstance = new window.google.maps.Map(mapRef.current, {
          center: dubaiCenter,
          zoom: 20,
          minZoom: 8,
          maxZoom: 20,
          mapTypeId: window.google.maps.MapTypeId.ROADMAP,
          clickableIcons: false, // POI 클릭 비활성화
          styles: [
            {
              featureType: "poi",
              elementType: "labels",
              stylers: [{ visibility: "off" }] // POI 라벨 숨기기
            },
            {
              featureType: "poi.business",
              stylers: [{ visibility: "off" }] // 비즈니스 POI 숨기기
            },
            {
              featureType: "poi.place_of_worship",
              stylers: [{ visibility: "off" }] // 종교 시설 숨기기
            },
            {
              featureType: "poi.school",
              stylers: [{ visibility: "off" }] // 학교 숨기기
            },
            {
              featureType: "poi.sports_complex",
              stylers: [{ visibility: "off" }] // 스포츠 시설 숨기기
            }
          ]
        });

        setMap(mapInstance);
        
        // 전역 변수로도 저장 (drawCadastralBoundary에서 사용)
        window.map = mapInstance;

        // InfoWindow 생성
        const infoWindowInstance = new window.google.maps.InfoWindow({
          pixelOffset: new window.google.maps.Size(0,10), // infoWindow를 폴리곤 중심에 맞추기 위한 오프셋
          disableAutoPan: false
        });
        setInfoWindow(infoWindowInstance);
        // 전역 변수로도 저장 (Information 컴포넌트에서 사용)
        window.infoWindow = infoWindowInstance;

        // 지도 클릭 이벤트 리스너
        mapInstance.addListener("click", (event) => {
          handleMapClick(event.latLng);
        });

        // 지도 로드 후 초기 위치의 지적도 정보 자동 로드
        setTimeout(() => {
          const initialLatLng = new window.google.maps.LatLng(25.202858375, 55.24999334);
          handleMapClick(initialLatLng);
        }, 500); // 지도가 완전히 로드된 후 500ms 지연
      } catch (error) {
        console.error("Error initializing map:", error);
        alert("An error occurred while initializing the map.");
      }
    };

    loadGoogleMaps();

    return () => {
      // 정리 작업
      polygonsRef.current.forEach((polygon) => polygon.setMap(null));
      markersRef.current.forEach((marker) => marker.setMap(null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMapClick = async (latLng) => {
    // 로딩 중이면 클릭 무시 (중복 요청 방지) - useRef로 동기적으로 체크
    if (loadingRef.current) {
      console.log("Already loading, ignoring click");
      return;
    }

    const lat = latLng.lat();
    const lng = latLng.lng();

    // 클릭한 위치 콘솔에 출력
    console.log("=== Map Click Location ===");
    console.log("Latitude:", lat);
    console.log("Longitude:", lng);
    console.log("Coordinates:", { lat, lng });
    console.log("=========================");

    // 로딩 상태를 즉시 설정 (동기적)
    loadingRef.current = true;
    setLoading(true);

    try {
      // 기존 폴리곤과 마커 제거
      clearMap();

      // 지적도 경계 정보 가져오기
      const cadastralResponse = await axios.get(`${API_BASE_URL}/dubai/cadastral/coordinates`, {
        params: {
          lng,
          lat,
          radius: 100, // 100미터 반경
        },
      });
      console.log("===== Map Click - Server Data Start =====");
      console.log("[Cadastral] Full Axios Response:", cadastralResponse);
      console.log("[Cadastral] Data:", cadastralResponse.data);
      console.log("[Cadastral] Data Type:", typeof cadastralResponse.data);
      console.log("[Cadastral] Is Array:", Array.isArray(cadastralResponse.data));
      console.log("[Cadastral] Data Length:", cadastralResponse.data?.length);

      let cadastral = null;
      let transaction = null;

      // 지적도 데이터가 있는지 확인 (배열이고 길이가 0보다 큰 경우)
      const hasCadastralData = cadastralResponse.data && 
                                Array.isArray(cadastralResponse.data) && 
                                cadastralResponse.data.length > 0;

      if (hasCadastralData) {
        cadastral = cadastralResponse.data[0];
        // setSelectedCadastral과 setShowInformation은 모든 데이터를 가져온 후에 호출

        console.log("Selected Cadastral:", cadastral);
        console.log("Geometry:", cadastral.geometry);
        console.log("Geometry Type:", cadastral.geometry?.type);
        console.log("Coordinates:", cadastral.geometry?.coordinates);

        // 모든 관련 정보 가져오기 (병렬 처리, 에러 발생해도 계속 진행)
        const fetchDataSafely = async (label, url, params) => {
          try {
            // 쿼리 파라미터를 숫자 형식의 문자열로 변환 (NestJS ValidationPipe가 기대하는 형식)
            const convertedParams = {};
            for (const [key, value] of Object.entries(params)) {
              if (value !== null && value !== undefined) {
                // 숫자인 경우 문자열로 변환 (axios가 자동으로 변환하지만 명시적으로 처리)
                if (typeof value === 'number') {
                  convertedParams[key] = value.toString();
                } else {
                  convertedParams[key] = value;
                }
              }
            }
            const response = await axios.get(url, { 
              params: convertedParams,
              paramsSerializer: {
                indexes: null // 배열 인덱스 제거
              }
            });
            console.log(`[${label}] Request Params:`, convertedParams);
            console.log(`[${label}] Data:`, response.data);
            return { success: true, data: response.data };
          } catch (error) {
            console.warn(`[${label}] API call failed for ${url}:`, {
              status: error.response?.status,
              statusText: error.response?.statusText,
              message: error.message,
              data: error.response?.data
            });
            return { success: false, data: null };
          }
        };

        const [
          transactionResult,
          buildingResult,
          zoningResult,
          urbanPlanningResult,
          townResult,
          villageResult
        ] = await Promise.all([
          fetchDataSafely('Transaction', `${API_BASE_URL}/dubai/transaction/coordinates`, { lng: lng.toString(), lat: lat.toString(), radius: '500' }),
          fetchDataSafely('Building', `${API_BASE_URL}/dubai/building/coordinates`, { lng: lng.toString(), lat: lat.toString(), radius: '500' }),
          fetchDataSafely('Zoning', `${API_BASE_URL}/dubai/zoning/point`, { lng: lng.toString(), lat: lat.toString() }),
          fetchDataSafely('UrbanPlanning', `${API_BASE_URL}/dubai/urban-planning/point`, { lng: lng.toString(), lat: lat.toString() }),
          fetchDataSafely('Town', `${API_BASE_URL}/dubai/town/point`, { lng: lng.toString(), lat: lat.toString() }),
          fetchDataSafely('Village', `${API_BASE_URL}/dubai/village/point`, { lng: lng.toString(), lat: lat.toString() })
        ]);

        // 최종 취합된 데이터 로그
        console.log("[Result] Transaction:", transactionResult);
        console.log("[Result] Building:", buildingResult);
        console.log("[Result] Zoning:", zoningResult);
        console.log("[Result] UrbanPlanning:", urbanPlanningResult);
        console.log("[Result] Town:", townResult);
        console.log("[Result] Village:", villageResult);

        // 거래 정보
        if (transactionResult.success && transactionResult.data && transactionResult.data.length > 0) {
          transaction = transactionResult.data[0];
          setTransactionData(transaction);
        } else {
          setTransactionData(null);
        }

        // 건물 정보
        if (buildingResult.success && buildingResult.data && buildingResult.data.length > 0) {
          setBuildingData(buildingResult.data[0]);
        } else {
          setBuildingData(null);
        }

        // 용도지역 정보
        if (zoningResult.success && zoningResult.data) {
          setZoningData(zoningResult.data);
        } else {
          setZoningData(null);
          setConstructionCosts({});
        }

        // 도시계획 정보
        if (urbanPlanningResult.success && urbanPlanningResult.data) {
          // 배열인 경우 첫 번째 요소 사용
          const urbanPlanning = Array.isArray(urbanPlanningResult.data) && urbanPlanningResult.data.length > 0
            ? urbanPlanningResult.data[0]
            : urbanPlanningResult.data;
          setUrbanPlanningData(urbanPlanning);
        } else {
          setUrbanPlanningData(null);
        }

        // 행정구역 정보
        if (townResult.success && townResult.data) {
          setTownData(townResult.data);
        } else {
          setTownData(null);
        }

        // 마을 정보
        if (villageResult.success && villageResult.data) {
          setVillageData(villageResult.data);
        } else {
          setVillageData(null);
        }

        // 용도지역 정보 확인 및 건축 유형과 공사비 가져오기
        if (zoningResult.success && zoningResult.data) {
          const zoning = Array.isArray(zoningResult.data) ? zoningResult.data[0] : zoningResult.data;
          const zoneName = zoning?.luse || zoning?.zoneType || zoning?.name;
          
          // 건축 유형과 공사비 가져오기
          if (zoneName && buildingTypes.length > 0) {
            await fetchConstructionCostsForZoning(zoneName);
          }
        }

        // 모든 데이터를 가져온 후에만 selectedCadastral 설정 및 Information과 InfoWindow 표시
        setSelectedCadastral(cadastral);
        setShowInformation(true);

        // 첫 번째 지적도 경계만 표시
        if (cadastral.geometry && cadastral.geometry.coordinates) {
          // map 상태가 설정될 때까지 약간의 지연
          if (!map) {
            console.log("Map not ready, waiting...");
            setTimeout(() => {
              drawCadastralBoundary(cadastral.geometry, latLng, cadastral, transaction);
            }, 100);
          } else {
            drawCadastralBoundary(cadastral.geometry, latLng, cadastral, transaction);
          }
        } else {
          console.error("No geometry data found in cadastral");
        }
      } else {
        // 지적도 데이터가 없는 경우 (빈 배열이거나 데이터가 없음)
        console.log("No cadastral data found. Response data:", cadastralResponse.data);
        
        // InfoWindow 닫기
        const currentInfoWindow = infoWindow || window.infoWindow;
        if (currentInfoWindow) {
          currentInfoWindow.close();
        }
        
        // 모든 데이터 초기화 및 Information 패널 닫기
        setSelectedCadastral(null);
        setTransactionData(null);
        setBuildingData(null);
        setZoningData(null);
        setUrbanPlanningData(null);
        setTownData(null);
        setVillageData(null);
        setConstructionCosts({});
        setShowInformation(false); // Information 패널 닫기
        
        // 사용자에게 알림 표시
        alert("No cadastral data found in this area. Please click another location.");
        console.log("Cadastral information not found at this location.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      alert("An error occurred while fetching data.");
    } finally {
      loadingRef.current = false; // 동기적으로 로딩 상태 해제
      setLoading(false);
    }
  };

  const drawCadastralBoundary = (geometry, centerLatLng, cadastral, transaction) => {
    // map 상태가 없으면 window.map을 사용 (초기화 시 저장)
    const currentMap = map || window.map;
    
    if (!currentMap || !geometry || !geometry.coordinates) {
      console.error("Invalid geometry data:", { 
        map: !!map,
        windowMap: !!window.map,
        currentMap: !!currentMap,
        mapRef: !!mapRef.current,
        geometry, 
        hasCoordinates: !!geometry?.coordinates 
      });
      return;
    }

    console.log("Drawing boundary with geometry type:", geometry.type);
    console.log("Full coordinates structure:", JSON.stringify(geometry.coordinates));

    let paths = [];

    // GeoJSON 형식에 따라 좌표 추출
    if (geometry.type === "Polygon") {
      // Polygon의 첫 번째 ring 사용 (외곽 경계)
      if (geometry.coordinates && geometry.coordinates[0]) {
        paths = geometry.coordinates[0].map((coord) => {
          // GeoJSON은 [lng, lat] 순서이므로 [lat, lng]로 변환
          return {
            lat: parseFloat(coord[1]),
            lng: parseFloat(coord[0]),
          };
        });
        console.log("Polygon paths created:", paths.length, "points");
      }
    } else if (geometry.type === "MultiPolygon") {
      // MultiPolygon 구조: coordinates[0] = 첫 번째 Polygon
      // coordinates[0][0] = 첫 번째 Polygon의 첫 번째 ring (외곽)
      // coordinates[0][0][0] = 첫 번째 좌표 [lng, lat]
      if (geometry.coordinates && geometry.coordinates[0] && geometry.coordinates[0][0]) {
        const firstRing = geometry.coordinates[0][0];
        console.log("First ring coordinates:", firstRing);
        paths = firstRing.map((coord) => {
          const lat = parseFloat(coord[1]);
          const lng = parseFloat(coord[0]);
          return { lat, lng };
        });
        console.log("MultiPolygon paths created:", paths.length, "points");
      }
    } else {
      console.error("Unsupported geometry type:", geometry.type);
      return;
    }

    if (paths.length === 0) {
      console.error("No paths created from geometry");
      return;
    }

    console.log("First path point:", paths[0]);
    console.log("Last path point:", paths[paths.length - 1]);
    console.log("All paths:", paths);

    // 폴리곤 생성
    try {
      // 경계 좌표 계산
      const bounds = new window.google.maps.LatLngBounds();
      paths.forEach((path) => {
        bounds.extend(new window.google.maps.LatLng(path.lat, path.lng));
      });
      
      console.log("Polygon bounds:", {
        north: bounds.getNorthEast().lat(),
        south: bounds.getSouthWest().lat(),
        east: bounds.getNorthEast().lng(),
        west: bounds.getSouthWest().lng()
      });

      const polygon = new window.google.maps.Polygon({
        paths: paths,
        strokeColor: "#AB35D5",
        strokeOpacity: 1.0,
        strokeWeight: 2,
        fillColor: "#AB35D5",
        fillOpacity: 0.1,
        map: currentMap,
        clickable: true,
        editable: false,
        draggable: false,
        zIndex: 1000, // 다른 레이어 위에 표시
      });

      polygonsRef.current.push(polygon);
      console.log("Polygon created and added to map");
      console.log("Polygon visible:", polygon.getVisible());
      console.log("Polygon map:", polygon.getMap());

      // 폴리곤 클릭 이벤트 추가 (디버깅용)
      polygon.addListener('click', (event) => {
        console.log("Polygon clicked at:", event.latLng);
      });

      // 폴리곤 중심으로 지도 부드럽게 이동 (줌 조정 없음)
      if (bounds) {
        const polygonCenter = bounds.getCenter();
        currentMap.panTo(polygonCenter);
        console.log("Map panned to polygon center:", polygonCenter.lat(), polygonCenter.lng());
      } else if (centerLatLng) {
        currentMap.panTo(centerLatLng);
        console.log("Map panned to clicked location");
      }
      
      // 폴리곤이 실제로 표시되는지 확인하기 위해 약간의 지연 후 체크
      setTimeout(() => {
        const polygonPath = polygon.getPath();
        if (polygonPath && polygonPath.getLength() > 0) {
          const pathBounds = new window.google.maps.LatLngBounds();
          polygonPath.forEach((point) => {
            pathBounds.extend(point);
          });
          console.log("Polygon path bounds confirmed:", {
            north: pathBounds.getNorthEast().lat(),
            south: pathBounds.getSouthWest().lat(),
            east: pathBounds.getNorthEast().lng(),
            west: pathBounds.getSouthWest().lng()
          });
          console.log("Polygon path length:", polygonPath.getLength());
          
          // 폴리곤의 첫 번째와 마지막 좌표 확인
          const firstPoint = polygonPath.getAt(0);
          const lastPoint = polygonPath.getAt(polygonPath.getLength() - 1);
          console.log("First point:", firstPoint.lat(), firstPoint.lng());
          console.log("Last point:", lastPoint.lat(), lastPoint.lng());
          
          // 폴리곤이 맵의 현재 뷰포트에 있는지 확인 (디버깅용만, 자동 조정 안 함)
          const mapBounds = currentMap.getBounds();
          if (mapBounds) {
            const isVisible = mapBounds.contains(pathBounds.getNorthEast()) || 
                            mapBounds.contains(pathBounds.getSouthWest());
            console.log("Polygon in viewport:", isVisible);
            // 사용자가 줌을 조정했을 수 있으므로 자동으로 다시 조정하지 않음
          }
        } else {
          console.warn("Polygon path not available");
        }
      }, 800);

      // 폴리곤의 중심 좌표를 전역 변수로 저장 (Information 컴포넌트에서 InfoWindow를 열 때 사용)
      if (bounds) {
        const center = bounds.getCenter();
        window.polygonCenter = {
          lat: center.lat(),
          lng: center.lng()
        };
        console.log("Polygon center saved:", window.polygonCenter);
      }
      
      // InfoWindow는 Information 컴포넌트에서 관리하므로 여기서는 열지 않음
    } catch (error) {
      console.error("Error creating polygon:", error);
      console.error("Error details:", error.message, error.stack);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const createInfoWindowContent = (cadastral, transaction) => {
    let content = '<div class="infoWindow">';
    
    if (cadastral && cadastral.landNo) {
      content += `<div class="name">${cadastral.landNo}</div>`;
    }
    
    if (transaction && transaction.totPrice) {
      content += `<div class="allow">Property Information</div>`;
      content += `<div class="estimateTotal">${formatPrice(transaction.totPrice)}</div>`;
    } else {
      content += `<div class="allow">Property Information</div>`;
      if (cadastral && cadastral.area) {
        content += `<div class="estimateTotal">${cadastral.area.toFixed(2)} m²</div>`;
      }
    }
    
    content += "</div>";
    return content;
  };

  const formatPrice = (price) => {
    if (!price) return "N/A";
    return new Intl.NumberFormat("en-AE", {
      style: "currency",
      currency: "AED",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const clearMap = () => {
    polygonsRef.current.forEach((polygon) => polygon.setMap(null));
    markersRef.current.forEach((marker) => marker.setMap(null));
    polygonsRef.current = [];
    markersRef.current = [];
    // const currentInfoWindow = infoWindow || window.infoWindow;
    // if (currentInfoWindow) {
    //   currentInfoWindow.close();
    // }
    // 모든 데이터 초기화
    setSelectedCadastral(null);
    setTransactionData(null);
    setBuildingData(null);
    setZoningData(null);
    setUrbanPlanningData(null);
    setTownData(null);
    setVillageData(null);
    setConstructionCosts({});
    // 폴리곤 중심 좌표도 초기화
    window.polygonCenter = null;
  };


  return (
    <div id="MapPage">
      <div className="google_map" ref={mapRef} style={{ width: '100vw', height: 'calc(100vh - 22px)' }}></div>
      <div className="header">
        <div className="contentWrap">
          <div className="imageBox btns">
            <img className="logo" src="/images/map/image_logo.svg" alt="Logo" />
          </div>
          <div className="inputBox">
            <input id="address" className="address" type="text" placeholder="البحث عن معلومات العقار..." />
          </div>
        </div>
      </div>
      <div className="side_menu">
        <div className="zoom_menu_wrap">
          <div className="btns plus_zoom" onClick={() => { if (map) map.setZoom(map.getZoom() + 1); }}></div>
          <div className="row"></div>
          <div className="btns minus_zoom" onClick={() => { if (map) map.setZoom(map.getZoom() - 1); }}></div>
        </div>
      </div>
      <Information 
        cadastralData={selectedCadastral}
        transactionData={transactionData}
        buildingData={buildingData}
        zoningData={zoningData}
        urbanPlanningData={urbanPlanningData}
        townData={townData}
        villageData={villageData}
        buildingTypes={buildingTypes}
        constructionCosts={constructionCosts}
        loadingModule={loading}
        setLoadingModule={setLoading}
        showInformation={showInformation}
      />
      <div className="footer">
        <div className="left">© 2024 PROPMATE Dubai</div>
        <div className="right">All Rights Reserved.</div>
      </div>
    </div>
  );
}

export default Map;

